import React, { useEffect, useRef } from 'react';
import { ArrowRight, Circle } from 'lucide-react';

// ---------------------------------------------------------------------------
// Hero: a LiDAR-style terrain of soft round points, rendered with WebGL at
// full device resolution. The camera flies slowly over it and an amber scan
// line sweeps across. The terrain stays in the lower third so the headline
// always sits on a clean background. Scrolling makes the camera dive while
// the rest of the page slides up over the hero (see the curtain wrapper in
// KhyaalTechLanding).
// ---------------------------------------------------------------------------

// Scroll distance (in viewport heights) the hero stays pinned for.
// Must match the curtain offset in KhyaalTechLanding (-mt-[100vh]) and h-[240vh].
const PIN_VH = 1.4;

const VERT = `
attribute vec2 aGrid;      // x in [-1,1], y in [0,1] (near -> far)
attribute float aSeed;
uniform mat4 uProj;
uniform mat4 uView;
uniform float uOffset;     // flight distance
uniform float uTime;
uniform float uReveal;     // 0..1 initial scan reveal (near -> far)
uniform float uDpr;
uniform float uSize;
uniform float uGain;
uniform float uWidth;     // half-width of the terrain in world units
varying float vAlpha;
varying float vBeam;

vec3 permute(vec3 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }
float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m; m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}
float fbm(vec2 p) {
  float s = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { s += a * snoise(p); p *= 2.03; a *= 0.5; }
  return s;
}
float terrain(vec2 p) {
  float ridge = 1.0 - abs(snoise(p * 0.07));
  float valley = pow(abs(p.x) * 0.11, 1.7) * 2.2;   // open valley down the middle
  return fbm(p * 0.16) * 0.9 + ridge * ridge * 0.9 * smoothstep(2.0, 9.0, abs(p.x)) + valley - 0.4;
}

void main() {
  float depth = aGrid.y;                       // 0 near, 1 far
  vec2 p = vec2(aGrid.x * uWidth, 3.0 - depth * 46.0);
  float h = terrain(vec2(p.x, p.y - uOffset));
  vec4 world = vec4(p.x, h, p.y, 1.0);
  vec4 view = uView * world;
  gl_Position = uProj * view;

  float dist = -view.z;
  gl_PointSize = clamp(uSize * uDpr * 9.0 / dist, 1.5 * uDpr, 4.0 * uDpr);

  // fog into the background, and fade the extreme sides
  float fog = pow(1.0 - depth, 1.1);
  float side = 1.0 - smoothstep(0.75, 1.0, abs(aGrid.x));
  // initial reveal: a scan front moving from near to far
  float reveal = smoothstep(uReveal, uReveal - 0.04, depth);
  float front = exp(-pow((depth - uReveal) * 40.0, 2.0)) * step(uReveal, 0.999);
  // recurring scan line every ~8s
  float sweep = exp(-pow((depth - fract(uTime / 8.0) * 1.15 + 0.05) * 55.0, 2.0));

  vBeam = clamp(front + sweep * 0.9, 0.0, 1.0);
  vAlpha = uGain * (0.22 + 0.7 * fog) * side * reveal * (0.7 + 0.3 * aSeed) + vBeam * fog * 0.8;
}
`;

const FRAG = `
precision mediump float;
uniform vec3 uInk;
uniform vec3 uAccent;
varying float vAlpha;
varying float vBeam;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float d = length(c);
  float soft = smoothstep(0.5, 0.15, d);
  if (soft <= 0.0) discard;
  gl_FragColor = vec4(mix(uInk, uAccent, vBeam), vAlpha * soft);
}
`;

// --- tiny matrix helpers (column-major, WebGL convention) -------------------
function perspective(fovy, aspect, near, far) {
  const f = 1 / Math.tan(fovy / 2);
  const nf = 1 / (near - far);
  return new Float32Array([f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0]);
}
function lookAt(eye, target) {
  const [ex, ey, ez] = eye;
  let zx = ex - target[0], zy = ey - target[1], zz = ez - target[2];
  let l = Math.hypot(zx, zy, zz); zx /= l; zy /= l; zz /= l;
  // up = (0,1,0); x = up × z
  let xx = zz, xy = 0, xz = -zx;
  l = Math.hypot(xx, xy, xz); xx /= l; xy /= l; xz /= l;
  const yx = zy * xz - zz * xy, yy = zz * xx - zx * xz, yz = zx * xy - zy * xx;
  return new Float32Array([
    xx, yx, zx, 0,
    xy, yy, zy, 0,
    xz, yz, zz, 0,
    -(xx * ex + xy * ey + xz * ez), -(yx * ex + yy * ey + yz * ez), -(zx * ex + zy * ey + zz * ez), 1
  ]);
}

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function compile(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
  return s;
}

export default function WorldHero({ counters, onStartProject }) {
  const canvasRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: true });
    if (!gl) return undefined; // no WebGL: the text-only hero still works

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');

    let prog;
    try {
      prog = gl.createProgram();
      gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
    } catch (e) {
      console.warn('WorldHero: WebGL unavailable', e);
      return undefined;
    }
    gl.useProgram(prog);

    // Grid of points: denser on desktop
    const mobile = window.innerWidth < 768;
    const cols = mobile ? 220 : 420;
    const rows = mobile ? 170 : 250;
    const grid = new Float32Array(cols * rows * 2);
    const seed = new Float32Array(cols * rows);
    let k = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        // small jitter avoids moiré from the perfectly regular grid
        grid[k * 2] = -1 + (2 * (c + 0.5 + (Math.random() - 0.5) * 0.6)) / cols;
        grid[k * 2 + 1] = (r + 0.5 + (Math.random() - 0.5) * 0.6) / rows;
        seed[k] = Math.random();
        k++;
      }
    }
    const bind = (name, data, size) => {
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, name);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
    };
    bind('aGrid', grid, 2);
    bind('aSeed', seed, 1);

    const U = {};
    ['uProj', 'uView', 'uOffset', 'uTime', 'uReveal', 'uDpr', 'uSize', 'uInk', 'uAccent', 'uGain', 'uWidth'].forEach((n) => {
      U[n] = gl.getUniformLocation(prog, n);
    });

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    const setColors = () => {
      if (darkQuery.matches) {
        gl.uniform3f(U.uInk, 232 / 255, 230 / 255, 227 / 255);
        gl.uniform3f(U.uAccent, 226 / 255, 170 / 255, 102 / 255);
        gl.uniform1f(U.uGain, 1.35); // light-on-dark points need a little more opacity
      } else {
        gl.uniform3f(U.uInk, 44 / 255, 36 / 255, 22 / 255);
        gl.uniform3f(U.uAccent, 190 / 255, 120 / 255, 50 / 255);
        gl.uniform1f(U.uGain, 1.0);
      }
    };
    setColors();
    darkQuery.addEventListener?.('change', setColors);

    let W = 0, H = 0, dpr = 1;
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = Math.floor(canvas.clientWidth * dpr);
      H = Math.floor(canvas.clientHeight * dpr);
      canvas.width = W;
      canvas.height = H;
      gl.viewport(0, 0, W, H);
      gl.uniform1f(U.uDpr, dpr);
      gl.uniform1f(U.uSize, mobile ? 1.15 : 1.0);
      // narrow screens only see a thin slice of the terrain; concentrate points there
      gl.uniform1f(U.uWidth, clamp((W / H) * 14, 7, 22));
    };
    resize();

    const mouse = { x: 0, y: 0, sx: 0, sy: 0 };
    const onMove = (e) => {
      mouse.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };

    let visible = true;
    const io = new IntersectionObserver(([en]) => { visible = en.isIntersecting; });
    io.observe(canvas);

    let raf = 0;
    let last = performance.now();
    const start = last;
    let offset = 0;
    let shown = 0;

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const time = (now - start) / 1000;

      const target = clamp(window.scrollY / (window.innerHeight * PIN_VH));
      shown += (target - shown) * (reduce ? 1 : 0.1);
      const p = ease(shown);

      // headline drifts up and fades as the camera dives
      if (textRef.current) {
        const o = 1 - clamp(shown / 0.45);
        textRef.current.style.opacity = o.toFixed(3);
        textRef.current.style.transform = `translate3d(0, ${(-shown * 120).toFixed(1)}px, 0)`;
        textRef.current.style.pointerEvents = o > 0.3 ? 'auto' : 'none';
      }
      if (!visible) return;

      if (!reduce) offset += dt * (0.9 + p * 3.0);
      mouse.sx += (mouse.x - mouse.sx) * 0.04;
      mouse.sy += (mouse.y - mouse.sy) * 0.04;

      const camY = 1.7 + p * 5.5 - mouse.sy * 0.15;
      // radians, + looks up; portrait screens look further down so the terrain
      // fills the empty space under the (taller) text block
      const pitch = (W < H ? -0.06 : 0.12) - p * 0.75;
      const yaw = mouse.sx * 0.05;
      const eye = [mouse.sx * 0.4, camY, 4.0];
      const tgt = [eye[0] + Math.sin(yaw), camY + Math.tan(pitch), eye[2] - Math.cos(yaw)];

      gl.uniformMatrix4fv(U.uProj, false, perspective((45 * Math.PI) / 180, W / H, 0.1, 120));
      gl.uniformMatrix4fv(U.uView, false, lookAt(eye, tgt));
      gl.uniform1f(U.uOffset, offset);
      gl.uniform1f(U.uTime, reduce ? 0 : time);
      gl.uniform1f(U.uReveal, reduce ? 1.2 : clamp(time / 2.8) * 1.2);

      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.POINTS, 0, cols * rows);
    };
    raf = requestAnimationFrame(frame);

    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMove);
      darkQuery.removeEventListener?.('change', setColors);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []);

  return (
    <section className="relative h-[240vh]" aria-label="Introduction">
      <div className="sticky top-0 h-screen overflow-hidden">
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />
        {/* keeps the nav and headline area perfectly clean */}
        <div className="absolute inset-x-0 top-0 h-[48%] pointer-events-none bg-gradient-to-b from-[#f7f4ed] via-[#f7f4ed]/70 to-transparent dark:from-[#0f0f0f] dark:via-[#0f0f0f]/70" />

        <div ref={textRef} className="relative z-10 h-full flex flex-col items-center px-6 sm:px-8 pt-[19vh] sm:pt-[21vh] text-center will-change-transform">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#2c2416]/10 dark:border-[#e8e6e3]/10 opacity-0 animate-[fadeIn_1s_ease-out_0.3s_forwards]">
            <Circle className="w-2 h-2 fill-current opacity-60" />
            <span className="text-[11px] tracking-widest uppercase opacity-70">AI Vision Laboratory</span>
          </div>

          <h1 className="mt-7 text-[clamp(2.6rem,7vw,6rem)] font-extralight leading-[0.98] tracking-[-0.02em] opacity-0 animate-[fadeInUp_1.1s_ease-out_0.45s_forwards]">
            Pioneering the
            <br />
            <span className="font-light">future of vision</span>
          </h1>

          <p className="mt-7 text-[16px] sm:text-[17px] font-light leading-relaxed text-[#2c2416]/75 dark:text-[#e8e6e3]/75 max-w-[620px] opacity-0 animate-[fadeIn_1s_ease-out_0.8s_forwards]">
            We build computer vision, 3D reconstruction and LLM systems for enterprises that demand technical excellence.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center gap-3 opacity-0 animate-[fadeIn_1s_ease-out_1s_forwards]">
            <a
              href="#portfolio"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2c2416] text-[#f7f4ed] dark:bg-[#e8e6e3] dark:text-[#0f0f0f] text-[12px] tracking-widest uppercase hover:gap-3 transition-all duration-500"
            >
              Explore our work <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <button
              onClick={onStartProject}
              className="inline-flex items-center px-6 py-3 rounded-full border border-[#2c2416]/20 dark:border-[#e8e6e3]/20 text-[12px] tracking-widest uppercase hover:bg-[#2c2416]/5 dark:hover:bg-[#e8e6e3]/5 transition-all duration-500"
            >
              Start a project
            </button>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex justify-center gap-12 px-10 py-4 rounded-2xl border border-[#2c2416]/10 dark:border-[#e8e6e3]/10 bg-[#f7f4ed]/75 dark:bg-[#0f0f0f]/70 backdrop-blur-md opacity-0 animate-[fadeIn_1s_ease-out_1.3s_forwards]">
            {[
              [`${counters.clients}+`, 'Enterprise clients'],
              [`${counters.satisfaction}%`, 'Satisfaction'],
              [`${counters.support}/7`, 'Support']
            ].map(([v, l]) => (
              <div key={l} className="text-center">
                <p className="text-2xl font-extralight tabular-nums">{v}</p>
                <p className="text-[10px] tracking-widest uppercase opacity-60 mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
